import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import '../../assets/styles/account/account.scss';
import { useTranslation } from "react-i18next";
import usePageMeta from '../../services/page-meta';

const EMAIL_OR_PHONE_REGEX = /^((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))|((.+)?([1-9][0-9][0-9]{7}))$/;
const ALPHA_NUMERIC_REGEX = /^[a-zA-Z0-9]+$/;
const DEFAULT_NON_MEDICAL_SPECIALITY = '124';
const MEDICAL_ROLE_VALUES = ['doctor', 'nurse', 'pharmacist', 'paramedic'];
const REGISTRATION_ROLE_VALUES = [...MEDICAL_ROLE_VALUES, 'pharma_company', 'patient'];

function resolvePasswordStrength(password, t) {
    if (!password) {
        return {
            percent: 0,
            tone: 'empty',
            label: t('registration.password.strength.empty'),
        };
    }

    let score = 0;

    if (password.length >= 6) {
        score += 35;
    }

    if (password.length >= 10) {
        score += 20;
    }

    if (/[a-z]/.test(password)) {
        score += 15;
    }

    if (/[A-Z]/.test(password)) {
        score += 15;
    }

    if (/\d/.test(password)) {
        score += 15;
    }

    const percent = Math.min(score, 100);

    if (percent < 50) {
        return {
            percent,
            tone: 'weak',
            label: t('registration.password.strength.weak'),
        };
    }

    if (percent < 80) {
        return {
            percent,
            tone: 'medium',
            label: t('registration.password.strength.medium'),
        };
    }

    return {
        percent,
        tone: 'strong',
        label: t('registration.password.strength.strong'),
    };
}

export default function Registration() {
    const { t } = useTranslation();
    usePageMeta({
        title: t('meta.registration.title', t('registration.header')),
        description: t('meta.registration.description', t('app.description')),
        keywords: t('meta.registration.keywords', t('app.keywords')),
    });

    const location = useLocation();
    const isPharmRoute = location.pathname === '/account/registration/pharm';
    const referrer = useMemo(() => new URLSearchParams(location.search).get('referrer') || '', [location.search]);
    const roleOptions = useMemo(() => ([
        {
            label: t('registration.roleGroups.medical'),
            options: [
                { value: 'doctor', label: t('registration.labels.isDoctor') },
                { value: 'nurse', label: t('registration.labels.isNurse') },
                { value: 'pharmacist', label: t('registration.labels.isPharmacist') },
                { value: 'paramedic', label: t('registration.labels.isParamedic') },
            ],
        },
        {
            label: t('registration.roleGroups.organizations'),
            options: [
                { value: 'pharma_company', label: t('registration.labels.isPharmaCompany') },
            ],
        },
        {
            label: t('registration.roleGroups.other'),
            options: [
                { value: 'patient', label: t('registration.labels.isPatient') },
            ],
        },
    ]), [t]);

    const schema = yup.object().shape({
        email: yup.string()
            .transform((value) => (typeof value === 'string' ? value.replace(/[()\s]+/g, '') : value))
            .required(t('login.errors.required'))
            .matches(EMAIL_OR_PHONE_REGEX, { message: t('login.errors.emailOrPhone') })
            .test(
                'accountExists',
                t('login.errors.accountExists'),
                async (value) => {
                    if (!value) {
                        return true;
                    }

                    const response = await fetch('/api/v1/account/registration/checkAccountJson', {
                        method: 'POST',
                        cache: 'no-cache',
                        credentials: 'same-origin',
                        body: JSON.stringify({ email: value })
                    });

                    return response.json();
                }
            ),
        password: yup.string()
            .required(t('login.errors.required'))
            .min(6, t('login.errors.passwordMinLength'))
            .max(16, t('login.errors.passwordMaxLength'))
            .matches(ALPHA_NUMERIC_REGEX, { message: t('registration.errors.wrongPasswordFormat') }),
        role: isPharmRoute
            ? yup.string().nullable()
            : yup.string()
                .required(t('registration.errors.roleRequired'))
                .oneOf(REGISTRATION_ROLE_VALUES, t('registration.errors.roleRequired')),
        speciality: yup.string().when('role', {
            is: (role) => !isPharmRoute && MEDICAL_ROLE_VALUES.includes(role),
            then: yup.string().required(t('login.errors.specialty')),
            otherwise: yup.string().nullable()
        }),
        personalData: yup.boolean().oneOf([true], t('login.errors.required')),
        userAgreement: yup.boolean().oneOf([true], t('login.errors.required')),
    });

    const { register, handleSubmit, watch, errors, setValue } = useForm(
        {
            resolver: yupResolver(schema),
            mode: 'onBlur',
            reValidateMode: 'onBlur',
            defaultValues: {
                role: isPharmRoute ? 'pharma_company' : '',
                speciality: isPharmRoute ? DEFAULT_NON_MEDICAL_SPECIALITY : '',
                personalData: !isPharmRoute,
                userAgreement: !isPharmRoute,
            }
        }
    );

    const [showPassword, setShowPassword] = useState(false);
    const [specialties, setSpecialties] = useState([]);
    const [submitError, setSubmitError] = useState(false);

    const selectedRole = isPharmRoute ? 'pharma_company' : (watch('role') || '');
    const selectedSpeciality = watch('speciality') || '';
    const passwordValue = watch('password') || '';
    const requiresSpeciality = !isPharmRoute && MEDICAL_ROLE_VALUES.includes(selectedRole);
    const passwordStrength = useMemo(() => resolvePasswordStrength(passwordValue, t), [passwordValue, t]);

    useEffect(() => {
        if (isPharmRoute) {
            setValue('role', 'pharma_company');
            setValue('speciality', DEFAULT_NON_MEDICAL_SPECIALITY);
            return;
        }

        if (!selectedRole) {
            setValue('speciality', '');
            return;
        }

        if (MEDICAL_ROLE_VALUES.includes(selectedRole)) {
            if (selectedSpeciality === DEFAULT_NON_MEDICAL_SPECIALITY) {
                setValue('speciality', '');
            }
            return;
        }

        setValue('speciality', DEFAULT_NON_MEDICAL_SPECIALITY);
    }, [isPharmRoute, selectedRole, selectedSpeciality, setValue]);

    useEffect(() => {
        fetch('/api/v1/dictionaries/medicalSpecialties')
            .then(res => res.json())
            .then((result) => {
                setSpecialties(result);
            });
    }, []);

    const onSubmit = (data) => {
        setSubmitError(false);

        const normalizedEmail = (data.email || '').replace(/[()\s]+/g, '');
        const role = isPharmRoute ? 'pharma_company' : data.role;
        const endpoint = isPharmRoute ? '/api/v1/account/registration/pharm' : '/api/v1/account/registration';

        const formData = new FormData();
        formData.append('email', normalizedEmail);
        formData.append('password', data.password);

        if (!isPharmRoute) {
            formData.append('role', role || '');
            formData.append('speciality', MEDICAL_ROLE_VALUES.includes(role) ? (data.speciality || '') : DEFAULT_NON_MEDICAL_SPECIALITY);
        }

        if (referrer) {
            formData.append('referrer', referrer);
        }

        return fetch(endpoint, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            body: formData,
        }).then((response) => {
            if (response.ok) {
                window.location.reload();
                return;
            }

            setSubmitError(true);
        });
    };

    return (
        <section className="section flex-center">
            <div className="authentication-form registration-form registration-form--modern">
                <div className="registration-head">
                    <h1>{t('registration.header')}</h1>
                    <p className="registration-subtitle">{t('registration.subtitle')}</p>
                </div>

                {!isPharmRoute && (
                    <>
                        <div className="registration-socials">
                            <button
                                className="registration-socials__button registration-socials__button--google"
                                type="button"
                                onClick={() => window.location.href = '/connect/google'}
                            >
                                <span className="registration-socials__icon registration-socials__icon--google" aria-hidden="true">G</span>
                                {t('buttons.googleLogin')}
                            </button>

                            <button
                                className="registration-socials__button registration-socials__button--facebook"
                                type="button"
                                onClick={() => window.location.href = '/connect/facebook'}
                            >
                                <span className="registration-socials__icon registration-socials__icon--facebook" aria-hidden="true">f</span>
                                {t('buttons.facebookLogin')}
                            </button>
                        </div>

                        <div className="registration-divider">
                            <span>{t('registration.divider')}</span>
                        </div>
                    </>
                )}

                <form id="registrationForm" className="registration-shell" onSubmit={handleSubmit(onSubmit)} noValidate>
                    {referrer && <input type="hidden" name="referrer" value={referrer} />}

                    <div className="registration-field">
                        <label className="registration-field__label" htmlFor="emailRegistration">
                            {t('registration.labels.username')}
                        </label>
                        <div className={`registration-field__control${errors.email ? ' is-error' : ''}`}>
                            <input
                                type="text"
                                name="email"
                                id="emailRegistration"
                                ref={register}
                                className="registration-field__input"
                                autoComplete="username"
                                placeholder={t('registration.labels.usernamePlaceholder')}
                            />
                        </div>
                        {errors.email ? (
                            <span className="registration-field__error">{errors.email.message}</span>
                        ) : (
                            <span className="registration-field__hint">{t('registration.labels.usernameDescription')}</span>
                        )}
                    </div>

                    <div className="registration-field">
                        <label className="registration-field__label" htmlFor="passwordRegistration">
                            {t('registration.labels.password')}
                        </label>
                        <div className={`registration-field__control registration-field__control--with-action${errors.password ? ' is-error' : ''}`}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                id="passwordRegistration"
                                ref={register}
                                className="registration-field__input"
                                autoComplete="new-password"
                                placeholder={t('registration.password.placeholder')}
                            />
                            <button
                                type="button"
                                className="registration-field__action"
                                onClick={() => setShowPassword((current) => !current)}
                            >
                                {showPassword ? t('registration.password.hide') : t('registration.password.show')}
                            </button>
                        </div>

                        <div className="registration-strength" aria-label={t('registration.password.strength.label')}>
                            <div className="registration-strength__bar">
                                <span
                                    className={`registration-strength__fill is-${passwordStrength.tone}`}
                                    style={{ width: `${passwordStrength.percent}%` }}
                                />
                            </div>
                            <span className="registration-strength__label">{passwordStrength.label}</span>
                        </div>

                        {errors.password ? (
                            <span className="registration-field__error">{errors.password.message}</span>
                        ) : (
                            <span className="registration-field__hint">{t('registration.password.description')}</span>
                        )}
                    </div>

                    {!isPharmRoute && (
                        <div className="registration-field">
                            <label className="registration-field__label" htmlFor="roleRegistration">
                                {t('registration.labels.role')}
                            </label>
                            <div className={`registration-field__control registration-field__control--select${errors.role ? ' is-error' : ''}`}>
                                <select
                                    id="roleRegistration"
                                    name="role"
                                    ref={register}
                                    className="registration-field__select"
                                    defaultValue=""
                                >
                                    <option value="" disabled>{t('registration.labels.rolePlaceholder')}</option>
                                    {roleOptions.map((group) => (
                                        <optgroup label={group.label} key={group.label}>
                                            {group.options.map((option) => (
                                                <option value={option.value} key={option.value}>{option.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                            {errors.role ? (
                                <span className="registration-field__error">{errors.role.message}</span>
                            ) : (
                                <span className="registration-field__hint">{t('registration.labels.roleDescription')}</span>
                            )}
                        </div>
                    )}

                    {requiresSpeciality && (
                        <div className="registration-field">
                            <label className="registration-field__label" htmlFor="speciality">
                                {t('registration.labels.specialty')}
                            </label>
                            <div className={`registration-field__control registration-field__control--select${errors.speciality ? ' is-error' : ''}`}>
                                <select
                                    id="speciality"
                                    name="speciality"
                                    ref={register}
                                    className="registration-field__select"
                                    defaultValue=""
                                >
                                    <option value="">{t('registration.labels.specialtyPlaceholder')}</option>
                                    {specialties.map((specialty) => (
                                        <option value={specialty.id} key={specialty.id}>{specialty.name}</option>
                                    ))}
                                </select>
                            </div>
                            { errors.speciality && <span className="registration-field__error">{errors.speciality.message}</span> }
                        </div>
                    )}

                    <div className="registration-consents">
                        <div className="registration-check-row">
                            <label className="registration-check" htmlFor="personalData">
                                <input ref={register} type="checkbox" name="personalData" id="personalData" />
                                <span dangerouslySetInnerHTML={{__html: t('registration.labels.privacyAgreement')}} />
                            </label>
                            { errors.personalData && <span className="registration-check__error">{errors.personalData.message}</span> }
                        </div>

                        <div className="registration-check-row">
                            <label className="registration-check" htmlFor="userAgreement">
                                <input ref={register} type="checkbox" name="userAgreement" id="userAgreement" />
                                <span>
                                    <a
                                        target="_blank"
                                        href="/documents/document.pdf"
                                        rel="noreferrer"
                                        className="label-link"
                                    >
                                        {t('registration.labels.termsAgreement')}
                                    </a>
                                </span>
                            </label>
                            { errors.userAgreement && <span className="registration-check__error">{errors.userAgreement.message}</span> }
                        </div>
                    </div>

                    {submitError && (
                        <div className="registration-submit-error">
                            <div className="registration-submit-error__text">{t('registration.errors.incorrectData')}</div>
                        </div>
                    )}

                    <div className="submit-block">
                        <button type="submit" className="registration-submit">
                            {t('buttons.registration')}
                        </button>
                    </div>

                    <div className="registration-footer">
                        <span>{t('registration.footer')}</span>
                        <Link rel="login-form" to="/account/login" className="registration-footer__link js-link-to-form">
                            {t('buttons.login')}
                        </Link>
                    </div>
                </form>
            </div>
        </section>
    );
}
